import React from 'react';
import {
	DocumentIcon,
	PencilIcon,
	TrashIcon,
	XMarkIcon,
} from '@heroicons/react/24/outline';
import moment from 'moment';

import { StackableModal } from '../components/nextgen';
import Button from '../components/Button';
import useModal from '../hooks/useModal';

const MODAL_NAME = 'documentPreview';

// Check if file is an image by name or content type
const isImageFile = (doc) => {
	if (doc?.contentType?.startsWith('image/')) return true;
	if (doc?.type?.startsWith('image/')) return true;
	if (!doc?.name) return false;
	const ext = doc.name.split('.').pop()?.toLowerCase();
	const imageExts = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
	return imageExts.includes(ext);
};

// Check if file is a PDF
const isPdfFile = (doc) => {
	if (doc?.contentType === 'application/pdf') return true;
	if (doc?.type === 'application/pdf') return true;
	if (!doc?.name) return false;
	const ext = doc.name.split('.').pop()?.toLowerCase();
	return ext === 'pdf';
};

// Format file size
const formatFileSize = (bytes) => {
	if (!bytes) return '';
	const size = parseInt(bytes, 10);
	if (isNaN(size)) return bytes;
	if (size < 1024) return `${size} B`;
	if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
	return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const DocumentPreviewModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);

	// Document can have different property names depending on source
	// Support both direct document object and options object
	const doc = open?.document || open;
	const enableEdit = open?.enableEdit || false;
	const enableDelete = open?.enableDelete || false;
	const showMetaData =
		open?.showMetaData !== undefined ? open.showMetaData : true;
	const onEdit = open?.onEdit;
	const onDelete = open?.onDelete;
	const onRemove = open?.onRemove;

	const name = doc?.name || doc?.originalName || 'Preview';
	const url = doc?.url || doc?.location || doc?.previewUrl;
	const size = doc?.size;

	const isImage = isImageFile(doc);
	const isPdf = isPdfFile(doc);

	const handleEdit = () => {
		if (onEdit) {
			onEdit(doc);
		}
	};

	const handleDelete = () => {
		if (onDelete) {
			onDelete(doc);
		}
	};

	const handleRemove = () => {
		if (onRemove) {
			onRemove(doc);
			setClose();
		}
	};

	return (
		<StackableModal
			open={!!open}
			onClose={setClose}
			title={!showMetaData ? name : ''}
			size={showMetaData ? '6xl' : '4xl'}
		>
			{doc && (
				<div
					className={showMetaData ? 'flex gap-6' : 'flex flex-col items-center'}
				>
					{/* Main Content Area */}
					<div className={showMetaData ? 'flex-1' : 'w-full'}>
						{/* Image preview */}
						{isImage && url && (
							<img
								src={url}
								alt={name}
								className="max-w-full max-h-[70vh] object-contain rounded-lg"
							/>
						)}

						{/* PDF preview */}
						{isPdf && url && (
							<iframe
								src={url}
								title={name}
								className="w-full h-[70vh] rounded-lg border border-gray-200"
							/>
						)}

						{/* Other file types */}
						{!isImage && !isPdf && (
							<div className="flex flex-col items-center justify-center py-12 text-center">
								<DocumentIcon className="w-16 h-16 text-gray-400 mb-4" />
								<p className="text-gray-600 mb-2">{name}</p>
								{size && (
									<p className="text-sm text-gray-500 mb-4">
										{formatFileSize(size)}
									</p>
								)}
								{url && (
									<a
										href={url}
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700"
									>
										Open in New Tab
									</a>
								)}
							</div>
						)}

						{/* Buttons at bottom when sidebar is hidden */}
						{!showMetaData && (enableEdit || enableDelete) && (
							<div className="mt-6 pt-4 border-t border-gray-200 w-full flex justify-center gap-3">
								{enableEdit && (
									<Button
										type="secondary"
										size="sm"
										Icon={PencilIcon}
										onClick={handleEdit}
									>
										Edit Document
									</Button>
								)}
								{enableDelete && (
									<Button
										type="danger"
										size="sm"
										Icon={TrashIcon}
										onClick={handleDelete}
									>
										Delete Document
									</Button>
								)}
							</div>
						)}
					</div>

					{/* Metadata Sidebar */}
					{showMetaData && (
						<div className="w-80 flex-shrink-0 pl-6">
							<div className="space-y-6 flex flex-col h-full">
								{/* File Information */}
								<div className="flex-1">
									<h3 className="text-sm font-semibold text-gray-900 mb-3">
										File Information
									</h3>
									<dl className="space-y-2">
										<div>
											<dt className="text-xs font-medium text-gray-500">
												Name
											</dt>
											<dd className="text-sm text-gray-900 mt-1 break-words">
												{name}
											</dd>
										</div>
										<div>
											<dt className="text-xs font-medium text-gray-500">
												Category
											</dt>
											<dd className="text-sm text-gray-900 mt-1">
												{doc?.category?.name || 'Uncategorized'}
											</dd>
										</div>
										{size && (
											<div>
												<dt className="text-xs font-medium text-gray-500">
													Size
												</dt>
												<dd className="text-sm text-gray-900 mt-1">
													{formatFileSize(size)}
												</dd>
											</div>
										)}
										{(doc?.contentType || doc?.type) && (
											<div>
												<dt className="text-xs font-medium text-gray-500">
													Type
												</dt>
												<dd className="text-sm text-gray-900 mt-1">
													{doc?.contentType || doc?.type}
												</dd>
											</div>
										)}
										{doc?.createdAt && (
											<div>
												<dt className="text-xs font-medium text-gray-500">
													Uploaded
												</dt>
												<dd className="text-sm text-gray-900 mt-1">
													{moment(doc.createdAt).format('MMM D, YYYY h:mm A')}
												</dd>
											</div>
										)}
										{doc?.updatedAt && doc.updatedAt !== doc.createdAt && (
											<div>
												<dt className="text-xs font-medium text-gray-500">
													Modified
												</dt>
												<dd className="text-sm text-gray-900 mt-1">
													{moment(doc.updatedAt).format('MMM D, YYYY h:mm A')}
												</dd>
											</div>
										)}
										{doc?.uploadedBy && (
											<div>
												<dt className="text-xs font-medium text-gray-500">
													Uploaded By
												</dt>
												<dd className="text-sm text-gray-900 mt-1">
													{doc.uploadedBy.firstName && doc.uploadedBy.lastName
														? `${doc.uploadedBy.firstName} ${doc.uploadedBy.lastName}`
														: 'Unknown'}
												</dd>
											</div>
										)}
									</dl>
								</div>

								{/* Actions */}
								{(enableEdit || enableDelete) && (
									<div className="pt-6">
										<div className="gap-x-2">
											<div className="gap-x-2 flex items-center justify-end">
												{enableEdit && (
													<Button
														type="secondary"
														size="sm"
														Icon={PencilIcon}
														onClick={handleEdit}
													>
														Edit
													</Button>
												)}
												{enableDelete && (
													<Button
														type="danger"
														size="sm"
														Icon={TrashIcon}
														onClick={handleDelete}
													>
														Delete
													</Button>
												)}
											</div>
										</div>
									</div>
								)}
							</div>
						</div>
					)}
				</div>
			)}
		</StackableModal>
	);
};

export default DocumentPreviewModal;
